import React, { forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import ActionItem from 'components/ActionItem/ActionItem.component';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import LinkIcon from 'icons/Link.icon';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import MailopenIcon from 'icons/Mailopen.icon';
import { ReactComponent as LinkedIn } from 'icons/linkedin.svg';
import { ReactComponent as Facebook } from 'icons/facebook.svg';
import {
  EmailShareButton,
  LinkedinShareButton,
  FacebookShareButton,
} from 'react-share';
import useTrackShareInitiated from 'hooks/useTrackShareInitiated';
import { LISTING_SHARE_MODE } from 'models/MixpanelEvents';
import { COLOR_VARIANT } from 'models/Colors';
import PhoneIcon from 'icons/Phone.icon';

import './ShareListingModal.styles.scss';

type ShareListingModalProps = {
  className?: string;
  url: string;
  listingId: string;
  listingName: string;
  onChooseLeads: () => void;
  onChooseContacts: () => void;
} & ModalProps;

const ShareListingModal: React.ForwardRefRenderFunction<
  ModalRef,
  ShareListingModalProps
> = (props, ref) => {
  const {
    className,
    url,
    listingId,
    listingName,
    onChooseLeads,
    onChooseContacts,
    ...rest
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('share-listing-modal', className);

  const { t } = useTranslation();

  const copyToClipboard = useCopyToClipboard();

  const trackShareInitiated = useTrackShareInitiated({
    listingId,
    listingName,
  });

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('shareListing')}
      {...rest}
    >
      <p>{t('shareListingDescription')}</p>

      <div className="share-listing-options">
        <p>{t('shareVia')}</p>

        <div>
          <Button
            onClick={(e) => {
              copyToClipboard(e, url);
              trackShareInitiated(LISTING_SHARE_MODE.CLIPBOARD);
            }}
          >
            <LinkIcon />
            <p>{t('copyLink')}</p>
          </Button>

          <EmailShareButton
            url={url}
            onClick={(_, link) => {
              trackShareInitiated(LISTING_SHARE_MODE.EMAIL);
              window.location.href = link;
            }}
          >
            <MailopenIcon />
            <p>{t('mail')}</p>
          </EmailShareButton>

          <LinkedinShareButton
            url={url}
            onClick={() => trackShareInitiated(LISTING_SHARE_MODE.LINKEDIN)}
          >
            <LinkedIn />
            <p>{t('linkedin')}</p>
          </LinkedinShareButton>

          <FacebookShareButton
            url={url}
            onClick={() => trackShareInitiated(LISTING_SHARE_MODE.FACEBOOK)}
          >
            <Facebook />
            <p>{t('facebook')}</p>
          </FacebookShareButton>
        </div>
      </div>

      <span className="border" />

      <p>{t('sendListingToPreviousLeads')}</p>

      <ActionItem
        title={t('chooseLeadsFromPreviousDeals')}
        description={t('chooseLeadsFromPreviousDealsDescription')}
        Icon={GroupUsersIcon}
        onClick={() => {
          onChooseLeads();
          modal?.close();
        }}
      />

      <ActionItem
        color={COLOR_VARIANT.ORANGE}
        title={t('chooseFromYourContacts')}
        description={t('selectSomeoneFromListOfContacts')}
        Icon={PhoneIcon}
        onClick={() => {
          onChooseContacts();
          modal?.close();
        }}
      />

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(ShareListingModal);
